import _ from 'lodash';
import moment from 'moment';
import { COMMUNICATED_TAG, NEVER_COMMUNICATED_TAG } from '../../state/search/search.state';
import { ONEI3_ASSET_TYPE_ATTR_KEY } from '../../constants';
import { ASSET_TYPE_VALUES, ASSET_TYPE_ATTRIBUTE } from '../../api/assetTypeAttribute';

export default function EquipmentSearchService() {
  //keys for facets dictionary access
  const FACET_SECURITY_GROUPS_KEY = 'securityGroups';
  const FACET_MAINTENANCE_KEY = 'maintenance';
  const FACET_ALERT_PRIORITY_KEY = 'alerts';
  const FACET_LAST_COMM_KEY = 'lastCommunicated';
  const SEARCH_OUTSIDE_BOUNDS = 'searchOutsideBounds';
  const FACET_LAST_SEEN_KEY = 'lastSeen';

  const groupsEnum = Object.freeze({
    security_groups: 'security groups',
    maintenance: 'maintenance',
    alerts: 'alerts',
    shares: 'shares',
  });

  function buildAPIEquipmentFilterObject(
    query = [],
    options,
    bounds,
    share,
    facets,
    tagFilters,
    modes,
    prefilter,
    sort = ['name:asc'],
    accountsArray,
  ) {
    let filter = {
      attrs: [],
      tags: [],
    };

    // pass the query search string
    filter.queries = query;

    if (options) {
      if (!isNaN(options.limit)) {
        filter.limit = options.limit;
      }
      if (!isNaN(options.since)) {
        filter.since = options.since;
      }
    }

    //build tags
    filter.tags = buildTags(facets, share);

    //build communicatedAt
    if (facets) {
      //last communicated
      if (facets[FACET_LAST_COMM_KEY]) {
        filter.communicatedAt = buildCommunicatedAt(facets[FACET_LAST_COMM_KEY]);
      }
      if (facets[FACET_LAST_SEEN_KEY]) {
        const [lastSeenFilter] = facets[FACET_LAST_SEEN_KEY] || [];
        filter.coordsAt = buildCoordAt(lastSeenFilter);
      }
    }
    //build attrs
    if (tagFilters) {
      filter.attrs = buildAttrs(tagFilters);
    }

    const isSearchOutsideBound = (modes || []).includes(SEARCH_OUTSIDE_BOUNDS);

    //build within boundary
    if (bounds && bounds.nw && bounds.se && !isSearchOutsideBound) {
      filter.within = {
        nw: [bounds.nw.lng, bounds.nw.lat],
        se: [bounds.se.lng, bounds.se.lat],
      };
    }

    if (isSearchOutsideBound) {
      filter.searchOutsideBound = true;
    }

    //build sort
    filter.sort = sort;

    //build accounts
    if (Array.isArray(accountsArray)) {
      filter.accounts = accountsArray;
    }

    if (prefilter != null) {
      filter = mergePreFilter(filter, prefilter);
    }

    return filter;
  }

  // private functions ---------------------------
  function buildTags(facets, share) {
    let tags = [];

    //build share tag
    if (share) {
      if (share.to) {
        tags.push(buildTagObject(`onei3:share:to:${share.to}`, groupsEnum.shares));
      }
      if (share.from) {
        tags.push(buildTagObject(`onei3:share:from:${share.from}`, groupsEnum.shares));
      }
    }

    //build facetted tags
    if (facets) {
      if (facets[FACET_SECURITY_GROUPS_KEY]) {
        tags = _.union(
          tags,
          buildSelectedTags(facets[FACET_SECURITY_GROUPS_KEY], groupsEnum.security_groups),
        );
      }
      if (facets[FACET_ALERT_PRIORITY_KEY]) {
        tags = _.union(
          tags,
          buildSelectedTags(facets[FACET_ALERT_PRIORITY_KEY], groupsEnum.alerts),
        );
      }
      if (facets[FACET_MAINTENANCE_KEY]) {
        tags = _.union(
          tags,
          buildSelectedTags(facets[FACET_MAINTENANCE_KEY], groupsEnum.maintenance),
        );
      }
    }

    return tags;
  }

  function buildSelectedTags(tagsArray, groupName) {
    let selectedTags = [];
    _.forEach(tagsArray, (facet) => {
      if (facet.tag && facet._selected) {
        selectedTags.push(buildTagObject(facet.tag, groupName));
      }
    });

    return selectedTags;
  }

  function buildTagObject(tagString, groupName) {
    let tagObject = {
      value: tagString || '',
    };

    if (groupName) {
      tagObject.group = groupName;
    }

    return tagObject;
  }

  function buildAttrValue(tagValue) {
    let obj = {};

    if (typeof tagValue !== 'object') {
      obj.equal = tagValue;
    } else if (tagValue.any) {
      obj.exists = true;
    } else if (tagValue.hasOwnProperty('max') && tagValue.hasOwnProperty('min')) {
      obj.between = {
        value1: tagValue.min,
        value2: tagValue.max,
      };
    } else if (!tagValue.hasOwnProperty('max') && tagValue.hasOwnProperty('min')) {
      obj.greaterThan = tagValue.min;
    } else if (tagValue.hasOwnProperty('max') && !tagValue.hasOwnProperty('min')) {
      obj.lessThan = tagValue.max;
    } else {
      obj.equal = tagValue;
    }

    return obj;
  }

  function buildAssetTypeAttr(tagKey, tagValue) {
    if (tagKey !== ASSET_TYPE_ATTRIBUTE.key) return;

    const assetTypeAttr = {
      name: ONEI3_ASSET_TYPE_ATTR_KEY,
    };

    if (tagValue.any) {
      assetTypeAttr.exists = true;
    } else if (tagValue === ASSET_TYPE_VALUES.Undefined) {
      assetTypeAttr.notExists = true;
    } else {
      assetTypeAttr.equal = tagValue.toLowerCase();
    }

    return assetTypeAttr;
  }

  function buildAttr(tagKey, tagValue) {
    let attr = {
      name: tagKey,
    };
    // Convert the human readable form for Asset Type to the id of the attr.
    // if (tagKey === ASSET_TYPE_ATTRIBUTE.key) {
    //   return buildAssetTypeAttr(tagKey, tagValue);
    // }

    Object.assign(attr, buildAttrValue(tagValue));
    return attr;
  }

  function buildAttrs(filters) {
    let attrs = [];

    _.forEach(filters, (tagValue, tagKey) => {
      if (!tagKey) return;
      if (typeof tagValue === 'undefined' && tagValue === null) return;

      if (_.isArray(tagValue)) {
        _.forEach(tagValue, (val) => {
          attrs.push(buildAttr(tagKey, val));
        });
      } else {
        attrs.push(buildAttr(tagKey, tagValue));
      }
    });

    return attrs;
  }

  function buildCommunicatedAt(facetCommunicated) {
    let communicatedAtObject = null;
    //last communicated
    if (facetCommunicated) {
      _.forEach(facetCommunicated, (facet) => {
        if (facet.tag && facet.range && facet._selected) {
          if (facet.tag === NEVER_COMMUNICATED_TAG) {
            communicatedAtObject = { never: true };
          } else if (facet.tag === COMMUNICATED_TAG) {
            communicatedAtObject = {
              within: facet.range.tag,
            };
          } else {
            // not communicated
            communicatedAtObject = {
              outside: facet.range.tag,
            };
          }
        }
      });
    }

    return communicatedAtObject;
  }

  function buildCoordAt(facetLastSeen = {}) {
    if (facetLastSeen) {
      const { min, max, type } = facetLastSeen.range || {};
      switch (type) {
        case 'outside':
          return { outside: `${min}d` };
        case 'within':
          return { within: `${max}d` };
        case 'between': {
          if (!isNaN(min) && !isNaN(max)) {
            const utcStateDate = moment.utc().subtract(max, 'days');
            const utcEndData = moment.utc().subtract(min, 'days');

            return {
              between: {
                start: utcStateDate,
                end: utcEndData,
              },
            };
          }
        }
      }
    }
    return null;
  }

  function mergePreFilter(filter, prefilter) {
    return _.mergeWith(filter, prefilter, (a, b) => {
      if (_.isArray(a)) {
        return a.concat(b);
      }

      return undefined;
    });
  }

  return {
    buildAPIEquipmentFilterObject: buildAPIEquipmentFilterObject,
  };
}
